import { TodaySection } from "./today-section";
import { EmptyState } from "@/components/v2/empty-state";
import { useActivityEventFeed } from "@/hooks/use-api";

// ADV-799: Recent Activity section. Uses the business-wide activity feed
// (/core/activity-events/feed with no entity_type/entity_id). The feed view
// already projects a (kind, label, body) shape with timestamp + entity link
// hints — render in place without coupling to the entity-scoped ActivityRow,
// which expects the comms-row schema instead.

interface ActivityFeedRow {
  id: string;
  entity_type?: string | null;
  entity_id?: string | null;
  kind: string;
  label: string;
  body: string | null;
  actor_type: string | null;
  actor_id: string | null;
  occurred_at: string;
}

interface FeedResponse {
  activity_events?: ActivityFeedRow[];
}

function SkeletonRows({ count = 4 }: { count?: number }) {
  return (
    <ul className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <li
          key={i}
          className="h-12 animate-pulse rounded-md bg-[color:var(--color-surface-2)]"
        />
      ))}
    </ul>
  );
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = Date.now();
  const diffMs = now - d.getTime();
  const hours = Math.floor(diffMs / 36e5);
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

interface SectionProps {
  title?: string;
}

export function RecentActivitySection({
  title = "Recent activity",
}: SectionProps = {}) {
  const { data, isLoading, isError } = useActivityEventFeed(20);
  const response = data as FeedResponse | undefined;
  const rows = response?.activity_events ?? [];

  return (
    <TodaySection
      id="today-section-activity"
      title={title}
      count={response ? rows.length : undefined}
    >
      {isLoading ? (
        <SkeletonRows />
      ) : isError ? (
        <EmptyState
          title="Couldn't load activity"
          description="Try refreshing the page."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No recent activity"
          description="Activity will appear here as stages, statuses, and notes change."
        />
      ) : (
        <ul className="divide-y divide-[color:var(--color-border)]">
          {rows.map((row) => (
            <li key={row.id} className="py-2 text-sm">
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-[color:var(--color-text-hi)]">
                  {row.label}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-[color:var(--color-text-md)]">
                  {formatTimestamp(row.occurred_at)}
                </span>
              </div>
              {row.body && (
                <p className="mt-1 line-clamp-2 text-[color:var(--color-text-md)]">
                  {row.body}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </TodaySection>
  );
}
